
// Deployment trigger: 2026-04-24
import React, { useState, useEffect } from 'react';
import { Mail, Search, Download, Loader2, UserX } from 'lucide-react';
import { Lead } from '../types';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';

const LeadsTable: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [showAllLeads, setShowAllLeads] = useState(false);

  useEffect(() => {
    setLoading(true);
    console.log(`[LeadsTable] Fetching leads. ProjectId: ${projectId}, ShowAll: ${showAllLeads}`);
    
    let q;
    if (showAllLeads) {
      // Troubleshooting mode: Fetch all leads (security rules will still restrict to what user can see)
      q = query(collection(db, 'leads'));
    } else {
      q = query(
        collection(db, 'leads'), 
        where('projectId', '==', projectId)
      );
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const leadsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Lead[];
      
      // Sort manually in memory
      leadsData.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      });

      console.log(`[LeadsTable] Successfully fetched ${leadsData.length} leads.`);
      setLeads(leadsData);
      setLoading(false);
    }, (err: any) => {
      console.error("[LeadsTable] Fetch error:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [projectId, showAllLeads]);

  const filteredLeads = leads.filter(l => 
    l.email.toLowerCase().includes(searchTerm.toLowerCase()) || 
    (l.name && l.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (l.prizeWon && l.prizeWon.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleExport = () => {
    const csv = [
      ['ID', 'Project ID', 'Campaign ID', 'Name', 'Email', 'Phone', 'Prize Won', 'Campaign Code', 'Date'],
      ...filteredLeads.map(l => [l.id, l.projectId || '', l.campaignId || '', l.name || '', l.email, l.phone || '', l.prizeWon || '', l.campaignCode || '', l.createdAt])
    ].map(e => e.join(",")).join("\n");
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `leads_export_${new Date().toISOString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-slate-200 shadow-sm">
         <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mb-4" />
         <p className="text-slate-400 font-bold text-xs uppercase tracking-widest">Loading Customer Leads...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Debug Info - Temporary */}
      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex items-center justify-between gap-4">
        <div className="text-[10px] font-mono text-slate-500">
          DEBUG: Viewing leads for Project ID: <span className="font-bold text-indigo-600">{projectId}</span>
          {showAllLeads && <span className="ml-2 text-amber-600 font-bold uppercase">[Troubleshooting Mode Active]</span>}
        </div>
        <button 
          onClick={() => setShowAllLeads(!showAllLeads)}
          className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${showAllLeads ? 'bg-amber-100 text-amber-700 border border-amber-200' : 'bg-slate-200 text-slate-600 border border-slate-300'}`}
        >
          {showAllLeads ? 'Disable Troubleshooting' : 'Troubleshoot Missing Leads'}
        </button>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input 
            type="text" 
            placeholder="Search leads by email or prize..." 
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-500 transition-all bg-white text-slate-800 text-sm shadow-sm font-medium"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={handleExport}
            disabled={filteredLeads.length === 0}
            className="flex items-center gap-2 px-6 py-3 border border-slate-200 rounded-xl hover:bg-slate-50 text-slate-600 font-bold text-xs transition-all justify-center disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            EXPORT CSV
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full text-left border-collapse min-w-[700px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">Customer</th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">Reward</th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">Campaign Code</th>
                {showAllLeads && <th className="px-6 py-4 text-[11px] font-bold text-amber-600 uppercase tracking-widest">Project ID</th>}
                <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">Phone</th>
                <th className="px-6 py-4 text-[11px] font-bold text-slate-500 uppercase tracking-widest">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-500 shrink-0">
                        <Mail className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-slate-800 truncate">{lead.name || 'Anonymous'}</div>
                        <div className="text-[10px] text-slate-400 font-medium truncate italic">{lead.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-md text-[10px] font-bold border border-emerald-100 uppercase">
                      {lead.prizeWon || 'No Prize'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs font-semibold text-slate-600 truncate">{lead.campaignCode || '—'}</div>
                  </td>
                  {showAllLeads && (
                    <td className="px-6 py-4">
                      <div className={`text-[10px] font-mono font-bold truncate ${lead.projectId === projectId ? 'text-indigo-600' : 'text-amber-600'}`}>
                        {lead.projectId || 'MISSING'}
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4">
                    <div className="text-xs font-semibold text-slate-600 truncate">{lead.phone || '—'}</div>
                  </td>
                  <td className="px-6 py-4 text-[10px] text-slate-400 font-bold uppercase">
                    {lead.createdAt}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredLeads.length === 0 && (
          <div className="px-6 py-20 text-center">
            <UserX className="w-12 h-12 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400 font-black text-xs uppercase tracking-widest">No leads captured yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadsTable;
